import React, { useState, useEffect } from 'react';
import { 
  School,
  Users, 
  GraduationCap, 
  ClipboardList,
  TrendingUp,
  Activity
} from 'lucide-react';
import dashboardService from '../services/dashboardService';

const Dashboard = () => {
  const [dados, setDados] = useState({
    escolas: { total: 0, crescimento: 0 },
    alunos: { total: 0, crescimento: 0 },
    usersAdmin: { total: 0, crescimento: 0 },
    testes: { total: 0, crescimento: 0 },
    conversas: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [atividadeRecente, setAtividadeRecente] = useState([]);
  
  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [dadosCompletos, atividade] = await Promise.all([
        dashboardService.getDadosCompletos(),
        dashboardService.getAtividadeRecente()
      ]);
      
      setDados(dadosCompletos);
      setAtividadeRecente(atividade);
    } catch (err) {
      console.error('Erro ao carregar dados do dashboard:', err);
      setError('Erro ao carregar estatísticas. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const formatarCrescimento = (crescimento) => {
    if (crescimento === 0) return 'sem alteração';
    const sinal = crescimento > 0 ? '+' : '';
    return `${sinal}${crescimento}`;
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="page-header">
          <h1 className="page-title">Painel de Controle</h1>
          <p className="page-subtitle">Carregando dados...</p>
        </div>
        <div className="loading-spinner">Carregando estatísticas...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="page-header">
          <h1 className="page-title">Painel de Controle</h1>
          <p className="page-subtitle">Erro ao carregar dados</p>
        </div>
        <div className="error-message">
          {error}
          <button onClick={carregarDados} className="retry-button">Tentar Novamente</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="page-header">
        <h1 className="page-title">Painel de Controle</h1>
        <p className="page-subtitle">Visão geral do sistema educacional</p>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-icon">
              <School size={24} />
            </div>
            <div className="stat-trend">
              <TrendingUp size={16} className={dados.escolas.crescimento > 0 ? 'trend-up' : 'trend-neutral'} />
            </div>
          </div>
          <div className="stat-value">{dashboardService.formatarNumero(dados.escolas.total)}</div>
          <div className="stat-label">Escolas Ativas</div>
          <div className={`stat-change ${dados.escolas.crescimento > 0 ? 'positive' : 'neutral'}`}>
            {formatarCrescimento(dados.escolas.crescimento)} este mês
          </div>
        </div>

        <div className="stat-card success">
          <div className="stat-header">
            <div className="stat-icon success">
              <GraduationCap size={24} />
            </div>
            <div className="stat-trend">
              <TrendingUp size={16} className={dados.alunos.crescimento > 0 ? 'trend-up' : 'trend-neutral'} />
            </div>
          </div>
          <div className="stat-value">{dashboardService.formatarNumero(dados.alunos.total)}</div>
          <div className="stat-label">Alunos Matriculados</div>
          <div className={`stat-change ${dados.alunos.crescimento > 0 ? 'positive' : 'neutral'}`}>
            {formatarCrescimento(dados.alunos.crescimento)} este mês
          </div>
        </div>

        <div className="stat-card warning">
          <div className="stat-header">
            <div className="stat-icon warning">
              <Users size={24} />
            </div>
            <div className="stat-trend">
              <Activity size={16} className="trend-neutral" />
            </div>
          </div>
          <div className="stat-value">{dashboardService.formatarNumero(dados.usersAdmin.total)}</div>
          <div className="stat-label">Users/Admin</div>
          <div className="stat-change neutral">
            {dados.usersAdmin.total > 0 ? 'Ativos no sistema' : 'Nenhum cadastrado'}
          </div>
        </div>

        <div className="stat-card danger">
          <div className="stat-header">
            <div className="stat-icon danger">
              <ClipboardList size={24} />
            </div>
            <div className="stat-trend">
              <TrendingUp size={16} className={dados.testes.crescimento > 0 ? 'trend-up' : 'trend-neutral'} />
            </div>
          </div>
          <div className="stat-value">{dashboardService.formatarNumero(dados.testes.total)}</div>
          <div className="stat-label">Testes Criados</div>
          <div className={`stat-change ${dados.testes.crescimento > 0 ? 'positive' : 'neutral'}`}>
            {formatarCrescimento(dados.testes.crescimento)} esta semana
          </div>
        </div>
      </div>

      {/* Atividade Recente e Resumo */}
      <div className="dashboard-grid">
        <div className="content-card">
          <h3 className="card-title">📊 Resumo do Sistema</h3>
          <div className="summary-stats">
            <div className="summary-item">
              <span className="summary-label">Total de Conversas Ativas:</span>
              <span className="summary-value">{dados.conversas}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Sistema:</span>
              <span className="summary-value status-active">✅ Operacional</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Última Atualização:</span>
              <span className="summary-value">{new Date().toLocaleString('pt-BR')}</span>
            </div>
          </div>
        </div>

        {atividadeRecente.length > 0 && (
          <div className="content-card">
            <h3 className="card-title">🕒 Atividade Recente</h3>
            <div className="activity-list">
              {atividadeRecente.slice(0, 5).map((atividade, index) => (
                <div key={atividade.id} className="activity-item">
                  <div className="activity-info">
                    <span className="activity-title">{atividade.titulo}</span>
                    <span className="activity-details">
                      {atividade.aluno} • {atividade.materia}
                    </span>
                  </div>
                  <span className="activity-time">
                    {new Date(atividade.data).toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .dashboard-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-top: 20px;
        }
        .stat-trend {
          margin-left: auto;
        }
        .trend-up {
          color: #10b981;
        }
        .trend-neutral {
          color: #6b7280;
        }
        .summary-stats {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .summary-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid #f3f4f6;
        }
        .summary-item:last-child {
          border-bottom: none;
        }
        .summary-label {
          color: #6b7280;
          font-size: 14px;
        }
        .summary-value {
          font-weight: 600;
          color: #374151;
        }
        .status-active {
          color: #10b981;
        }
        .activity-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .activity-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px;
          background: #f9fafb;
          border-radius: 8px;
          border-left: 3px solid #3b82f6;
        }
        .activity-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .activity-title {
          font-weight: 500;
          color: #374151;
          font-size: 14px;
        }
        .activity-details {
          font-size: 12px;
          color: #6b7280;
        }
        .activity-time {
          font-size: 11px;
          color: #9ca3af;
          white-space: nowrap;
        }
        .loading-spinner {
          text-align: center;
          padding: 40px;
          color: #6b7280;
        }
        .error-message {
          background: #fef2f2;
          color: #dc2626;
          padding: 20px;
          border-radius: 8px;
          text-align: center;
        }
        .retry-button {
          background: #dc2626;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          margin-top: 10px;
          cursor: pointer;
        }
        .retry-button:hover {
          background: #b91c1c;
        }
        .stat-change.neutral {
          color: #6b7280;
        }
        @media (max-width: 768px) {
          .dashboard-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
